import {
  fetchUserTaskInfo,
  UserTaskInfo,
  getAttemptAuditResponse,
  getAttemptResponse,
  queryClient,
  getProjectRoleForUser,
  getAttemptAuditMaxClaimTimes,
  getAuditErrorCategories,
  getAuditGradingInstructionsInternalUrl,
  getAuditErrorCategoriesToMaxScoreMap,
  getAuditorPermissions,
} from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditApi';
import { IAttemptAuditJson } from '@scale/shared/types/speedAudit';
import { ITeamLeadPodTypeJson } from '@scale/shared/types/expertManagement';
import { AttemptAuditMaxClaimTimeResponse } from '@scale/shared/types/corp/attemptAudit';
import { useQuery, UseQueryResult } from '@tanstack/react-query-v4';
import { ITaskAttemptToReturnJson } from '@scale/shared/types/taskattempt';
import { ProjectRole } from '@scale/shared/defaults';
import { fetchUserInfo } from 'react-scale/components/ExpertManagement/ExpertManagementPage/api';
import { getAttempterAuditScores } from './ChatBulkAuditApi';
import { defaultStaleTime } from './utils';

interface UseFetchUserInfoResponse {
  teamLeadPodType?: ITeamLeadPodTypeJson;
  id: string;
  name: string;
  email: string;
  role: string;
  [key: string]: $FIXME;
}

type UseQueryOptions = {
  refetchInterval?: number;
  staleTime?: number;
};

const queryFactory = {
  userTaskInfo: {
    list: ({
      contributorId,
      project,
      reviewLevel,
    }: {
      contributorId: string;
      project?: string;
      reviewLevel?: number;
    }) => ({
      queryKey: ['userTaskInfo', contributorId, project, reviewLevel] as const,
      queryFn: () => fetchUserTaskInfo(contributorId, project, reviewLevel),
    }),
  },
  attemptAuditResponse: {
    get: ({ attemptAuditId }: { attemptAuditId: string | undefined }) => ({
      queryKey: ['attemptAuditResponse', attemptAuditId] as const,
      queryFn: () => {
        return getAttemptAuditResponse(attemptAuditId);
      },
    }),
  },
  attemptResponse: {
    get: ({ attemptId }: { attemptId: string | undefined }) => ({
      queryKey: ['attemptResponse', attemptId] as const,
      queryFn: () => {
        return getAttemptResponse(attemptId);
      },
    }),
  },
  projectRoleForUser: {
    get: ({
      projectId,
      userId,
      roles,
    }: {
      projectId: string | undefined;
      userId: string | undefined;
      roles?: string[];
    }) => ({
      queryKey: ['projectRoleForUser', projectId, userId, roles] as const,
      queryFn: () => getProjectRoleForUser(projectId, userId, roles),
    }),
  },
  config: {
    getMaxClaimTimes: ({ auditBatchId }: { auditBatchId: string | undefined }) => ({
      queryKey: ['config', 'maxClaimTimes', auditBatchId] as const,
      queryFn: () => {
        return getAttemptAuditMaxClaimTimes(auditBatchId);
      },
    }),
  },
  userInfo: {
    get: ({ userId }: { userId: string }) => ({
      queryKey: ['userInfo', userId] as const,
      queryFn: () => {
        return fetchUserInfo(userId) as unknown as UseFetchUserInfoResponse;
      },
    }),
  },
  auditErrorCategories: {
    get: ({
      projectId,
      displayStandardizedErrorCategories,
    }: {
      projectId: string | undefined;
      displayStandardizedErrorCategories?: boolean;
    }) => ({
      queryKey: ['auditErrorCategories', projectId, displayStandardizedErrorCategories] as const,
      queryFn: () => {
        return getAuditErrorCategories(projectId, displayStandardizedErrorCategories);
      },
    }),
  },
  errorCategoriesToMaxScoreMap: {
    get: ({ projectId }: { projectId: string | undefined }) => ({
      queryKey: ['errorCategoriesToMaxScoreMap', projectId] as const,
      queryFn: () => {
        return getAuditErrorCategoriesToMaxScoreMap(projectId);
      },
    }),
  },
  auditGradingInstructionsInternalUrl: {
    get: ({ projectId }: { projectId?: string }) => ({
      queryKey: ['auditGradingInstructionsInternalUrl', projectId] as const,
      queryFn: () => {
        return getAuditGradingInstructionsInternalUrl(projectId);
      },
    }),
  },
  attempterAuditScores: {
    get: ({
      projectId,
      attempterId,
    }: {
      projectId: string | undefined;
      attempterId: string | undefined;
    }) => ({
      queryKey: ['attempterAuditScores', projectId, attempterId] as const,
      queryFn: () => {
        return getAttempterAuditScores(projectId, attempterId);
      },
    }),
  },
  auditorPermissions: {
    get: () => ({
      queryKey: ['auditorPermissions'] as const,
      queryFn: () => {
        return getAuditorPermissions();
      },
    }),
  },
};

export function useUserTaskInfo(
  contributorId: string,
  attempt: ITaskAttemptToReturnJson,
): UseQueryResult<UserTaskInfo, Error> {
  return useQuery<UserTaskInfo, Error>({
    ...queryFactory.userTaskInfo.list({
      contributorId,
      project: attempt?.project,
      reviewLevel: attempt?.attemptedAtReviewLevel,
    }),
    refetchInterval: 60 * 60 * 1000, // 1 hour
  });
}

export function useGetProjectRolesForUser(
  projectId: string | undefined,
  userId: string | undefined,
  roles?: string[],
): UseQueryResult<ProjectRole | undefined, Error> {
  return useQuery<ProjectRole | undefined, Error>({
    ...queryFactory.projectRoleForUser.get({
      projectId,
      userId,
      roles,
    }),
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });
}

export function useAuditAttemptResponse(
  attemptAuditId: string | undefined,
  queryOptions?: UseQueryOptions,
): UseQueryResult<IAttemptAuditJson | undefined, Error> {
  return useQuery<IAttemptAuditJson | undefined, Error>({
    ...queryFactory.attemptAuditResponse.get({
      attemptAuditId,
    }),
    ...queryOptions,
  });
}

export function useHistoricalResponseInAttemptAudit(
  attemptId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    ...queryFactory.attemptResponse.get({
      attemptId,
    }),
    staleTime: 30 * 24 * 60 * 60 * 1000, // 30 days; should be long enough to not fetch things
    enabled,
  });
}

export function useGetProjectClaimTimeMax(auditBatchId: string | undefined) {
  return useQuery<AttemptAuditMaxClaimTimeResponse | undefined, Error>({
    ...queryFactory.config.getMaxClaimTimes({
      auditBatchId,
    }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export const useFetchUserInfo = (userId: string) => {
  return useQuery<UseFetchUserInfoResponse, Error>({
    ...queryFactory.userInfo.get({ userId }),
  });
};

export const useAuditErrorCategories = (
  projectId: string | undefined,
  displayStandardizedErrorCategories?: boolean,
) => {
  return useQuery<string[], Error>({
    ...queryFactory.auditErrorCategories.get({ projectId, displayStandardizedErrorCategories }),
  });
};

export const useErrorCategoriesToMaxScoreMap = (projectId: string | undefined) => {
  return useQuery<Record<string, number>, Error>({
    ...queryFactory.errorCategoriesToMaxScoreMap.get({ projectId }),
  });
};

export const useInternalAuditGradingInstructions = (
  projectId?: string,
): UseQueryResult<{ internalUrl?: string; validationError?: string } | undefined, Error> => {
  return useQuery<{ internalUrl?: string; validationError?: string } | undefined, Error>({
    ...queryFactory.auditGradingInstructionsInternalUrl.get({ projectId }),
  });
};

export function prefetchAuditAttemptResponse(attemptAuditId: string): void {
  queryClient.prefetchQuery({
    ...queryFactory.attemptAuditResponse.get({
      attemptAuditId,
    }),
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

export function useAttempterAuditScores(
  projectId: string | undefined,
  attempterId: string | undefined,
) {
  return useQuery({
    ...queryFactory.attempterAuditScores.get({ projectId, attempterId }),
    staleTime: defaultStaleTime, // 1 hour
  });
}

export function useAuditorPermissions() {
  return useQuery({
    ...queryFactory.auditorPermissions.get(),
  });
}
